import {
  GetTransactionsWithEventsForSiteData,
  TransactionEventViewDto,
  TransactionReadable,
  TransactionState,
  TransactionViewDto,
} from "../../../services/client";
import Table, { ColumnsType } from "antd/es/table";
import Pagination from "antd/es/pagination";
import { unreachable } from "../../../util/unreachable";
import Tag from "antd/es/tag";
import { uniqBy } from "lodash";
import ErrorMessage from "../../util-components/ErrorMessage";
import { useCallback, useMemo, useState } from "react";
import type { TablePaginationConfig } from "antd";
import type { FilterValue } from "antd/es/table/interface";
import { useCursorPagination } from "../../../hooks/useCursorPagination";
import {
  CaretRightOutlined,
  CopyOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import Tooltip from "antd/es/tooltip";
import { useNotify } from "../../util-components/Notifications/notifications";
import { useGroupedTransactions } from "./useGroupedTransactions";
import { formatDateTime } from "../../../util/formatDate";
import { isViewAsAdmin } from "../../../util/viewAsAdmin";
import { EndAsGhostButton } from "./EndAsGhostButton";

export type TransactionFilters = Pick<
  NonNullable<GetTransactionsWithEventsForSiteData["query"]>,
  "asset_id" | "profile_id"
>;

type TransactionsTableBaseProps = {
  loading: boolean;
  error: boolean;
  onFilterChange?: (filters: TransactionFilters) => void;
};

type PaginatedTransactionsTableProps = TransactionsTableBaseProps & {
  siteId: string;
  pages: { data: TransactionViewDto[]; page_size: number }[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  transactions?: never;
};

type SimpleTransactionsTableProps = TransactionsTableBaseProps & {
  transactions: TransactionReadable[];
  siteId?: never;
  pages?: never;
  fetchNextPage?: never;
  hasNextPage?: never;
  isFetchingNextPage?: never;
};

type TransactionsTableProps =
  | PaginatedTransactionsTableProps
  | SimpleTransactionsTableProps;

const mapStateToTag = (state: TransactionState) => {
  switch (state) {
    case "Started":
      return <Tag color="green">Started</Tag>;
    case "Updated":
      return <Tag color="blue">Updated</Tag>;
    case "Ended":
      return <Tag color="red">Ended</Tag>;
    case "SuspendedEV":
      return <Tag color="orange">Suspended EV</Tag>;
    default:
      return unreachable(state);
  }
};

const renderDate = (date: string | null | undefined) =>
  date ? formatDateTime(date) : "";

const renderPower = (power: number | null | undefined) =>
  power ? `${(power / 1000).toLocaleString()} kW` : "";

const renderEnergy = (energy: number | null | undefined) =>
  energy ? `${(energy / 1000).toLocaleString()} kWh` : "";

// Unlike renderEnergy, distinguishes a real (possibly zero) amount from one
// the backend hasn't calculated yet, so a still-forming amount doesn't read
// as "definitely nothing was charged".
const renderCalculatedAmount = (energy: number | null | undefined) => {
  if (energy == null) {
    return "—";
  }
  const kwh = energy / 1000;
  return `${kwh.toLocaleString(undefined, {
    maximumFractionDigits: kwh > 100 ? 0 : 1,
  })} kWh`;
};

const CopyableId: React.FC<{ id: string | null | undefined }> = ({ id }) => {
  const notify = useNotify();

  if (!id) {
    return null;
  }

  const handleCopy = async (e: React.MouseEvent) => {
    // Prevent the row's onClick (e.g. expand toggle) from firing.
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      notify({
        type: "success",
        message: "Copied",
        description: `Transaction ID copied to clipboard.`,
        duration: 2,
      });
    } catch {
      notify({
        type: "error",
        message: "Copy failed",
        description: "Could not copy the transaction ID to the clipboard.",
      });
    }
  };

  return (
    <Tooltip title={`${id} — click to copy`}>
      <span
        onClick={handleCopy}
        style={{ cursor: "pointer", whiteSpace: "nowrap" }}
      >
        {id.split("-")[0]}
        <CopyOutlined style={{ marginLeft: 8 }} />
      </span>
    </Tooltip>
  );
};

// A flat row is either a transaction summary, one of its events, or a marker
// for the events the API left out of an over-long transaction.
type TransactionRow = { _kind: "tx"; _key: string } & TransactionViewDto;
type EventRow = {
  _kind: "event";
  _key: string;
  asset_id: string;
  profile_id: string;
} & TransactionEventViewDto;
type OmittedEventsRow = {
  _kind: "omitted";
  _key: string;
  asset_id: string;
  profile_id: string;
  transaction_id: string;
  omitted_event_count: number;
};
type FlatRow = TransactionRow | EventRow | OmittedEventsRow;

function flattenWithEvents(
  transactions: TransactionViewDto[],
  expanded: Set<string>,
): FlatRow[] {
  const rows: FlatRow[] = [];
  for (const tx of transactions) {
    rows.push({ _kind: "tx", _key: tx.transaction_id, ...tx });
    if (expanded.has(tx.transaction_id)) {
      // The API caps the events it returns for one transaction while
      // `event_count` stays the real total; the difference is what this
      // list can't show, flagged on top of the expanded events.
      const omittedEventCount = tx.event_count - tx.events.length;
      if (omittedEventCount > 0) {
        rows.push({
          _kind: "omitted",
          _key: `${tx.transaction_id}-omitted`,
          asset_id: tx.asset_id,
          profile_id: tx.profile_id,
          transaction_id: tx.transaction_id,
          omitted_event_count: omittedEventCount,
        });
      }
      for (let i = tx.events.length - 1; i >= 0; i--) {
        rows.push({
          _kind: "event",
          _key: `${tx.transaction_id}-evt-${i}`,
          ...tx.events[i],
          asset_id: tx.asset_id,
          profile_id: tx.profile_id,
        });
      }
    }
  }
  return rows;
}

// The accent color used to bracket a transaction and its (expanded) events
// together, making it visually obvious they're part of the same group.
const GROUP_ACCENT_COLOR = "#91caff";

function groupedColumns(
  transactions: TransactionViewDto[],
  expandedIds: Set<string>,
  // The `with-events` endpoint backing the paginated Site Details table
  // includes `calculated_amount_wh` on every transaction; the lifecycle feed
  // backing the simple (optimisation task) table doesn't, so that view
  // leaves this column out rather than showing it permanently empty.
  showCalculatedAmount: boolean,
  endAsGhostSiteId: string | undefined,
): ColumnsType<FlatRow> {
  const columns: ColumnsType<FlatRow> = [
    {
      title: "Asset",
      key: "asset_id",
      filterSearch: true,
      filters: uniqBy(
        transactions.map((item) => ({
          text: item.asset_id,
          value: item.asset_id,
        })),
        "value",
      ),
      onFilter: (value, record) => record.asset_id.startsWith(value as string),
      onCell: (record) => ({
        style: {
          whiteSpace: "nowrap",
          borderLeft:
            record._kind !== "tx" || expandedIds.has(record.transaction_id)
              ? `3px solid ${GROUP_ACCENT_COLOR}`
              : undefined,
        },
      }),
      render: (_, record) => {
        if (record._kind === "event") {
          return null;
        }
        if (record._kind === "omitted") {
          return (
            <span className="text-orange-500 font-medium">
              <WarningOutlined style={{ marginRight: 8 }} />
              {record.omitted_event_count} events in the middle of the
              transaction were removed because there were too many events.
            </span>
          );
        }
        const isExpanded = expandedIds.has(record.transaction_id);
        return (
          <span className="inline-flex items-center">
            <span className="inline-flex items-center justify-center w-5 h-5 mr-1 rounded-full transition-colors hover:bg-black/5">
              <CaretRightOutlined
                style={{
                  fontSize: 11,
                  color: "rgba(0, 0, 0, 0.45)",
                  transition: "transform 0.2s ease",
                  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                }}
              />
            </span>
            {record.asset_id}
          </span>
        );
      },
    },
    {
      title: "Transaction ID",
      key: "transaction_id",
      filterSearch: true,
      filters: uniqBy(
        transactions.map((item) => ({
          text: item.transaction_id,
          value: item.transaction_id,
        })),
        "value",
      ),
      onFilter: (value, record) =>
        record._kind === "tx" && record.transaction_id === value,
      render: (_, record) =>
        record._kind === "tx" ? (
          <CopyableId id={record.transaction_id} />
        ) : null,
    },
    {
      title: "State",
      key: "transaction_state",
      onCell: (record) => ({
        style: { textAlign: record._kind === "event" ? "center" : undefined },
      }),
      render: (_, record) => {
        switch (record._kind) {
          case "tx":
            return mapStateToTag(record.transaction_state);
          case "event":
            return <span className="text-gray-400">↳</span>;
          case "omitted":
            return null;
          default:
            return unreachable(record);
        }
      },
    },
    {
      title: "Events",
      key: "events",
      width: 110,
      render: (_, record) => {
        switch (record._kind) {
          case "tx":
            return `${record.event_count} events`;
          case "event":
            return <span className="text-gray-400">{record.event_name}</span>;
          case "omitted":
            return null;
          default:
            return unreachable(record);
        }
      },
    },
    {
      title: "Received at",
      key: "received_at",
      render: (_, record) => {
        switch (record._kind) {
          case "tx":
            return renderDate(record.latest_event_received_at);
          case "event":
            return renderDate(record.received_at);
          case "omitted":
            return null;
          default:
            return unreachable(record);
        }
      },
    },
    {
      title: "Started at",
      dataIndex: "started_at",
      key: "started_at",
      render: renderDate,
    },
    {
      title: "Planned departure",
      dataIndex: "estimated_departure_time",
      key: "estimated_departure_time",
      render: renderDate,
    },
    {
      title: "Actual departure",
      dataIndex: "stop_time",
      key: "stop_time",
      render: renderDate,
    },
    {
      title: "Requested Energy",
      dataIndex: "requested_min_energy",
      key: "requested_min_energy",
      render: renderEnergy,
    },
    {
      title: "Max Power",
      dataIndex: "max_power",
      key: "max_power",
      render: renderPower,
    },
    ...(showCalculatedAmount
      ? [
          {
            title: "Amount Charged",
            key: "calculated_amount",
            render: (_: unknown, record: FlatRow) =>
              record._kind === "tx"
                ? renderCalculatedAmount(record.calculated_amount_wh)
                : null,
          },
        ]
      : []),
    {
      title: "Profile ID",
      key: "profile_id",
      dataIndex: "profile_id",
      filterSearch: true,
      filters: uniqBy(
        transactions
          .filter((item) => item.profile_id)
          .map((item) => ({
            text: item.profile_id,
            value: item.profile_id,
          })),
        "value",
      ),
      onFilter: (value, record) => record.profile_id === value,
    },
    {
      title: "Badge ID",
      dataIndex: "badge_id",
      key: "badge_id",
    },
    ...(endAsGhostSiteId
      ? [
          {
            title: "",
            key: "end_as_ghost",
            render: (_: unknown, record: FlatRow) =>
              record._kind === "tx" && record.transaction_state !== "Ended" ? (
                <EndAsGhostButton
                  siteId={endAsGhostSiteId}
                  transactionId={record.transaction_id}
                />
              ) : null,
          },
        ]
      : []),
  ];

  // An omitted-events marker is a single message, so its first cell spans
  // the whole row and the remaining cells collapse.
  return columns.map((column, index) => ({
    ...column,
    onCell: (record: FlatRow, rowIndex?: number) => ({
      ...column.onCell?.(record, rowIndex),
      ...(record._kind === "omitted"
        ? { colSpan: index === 0 ? columns.length : 0 }
        : {}),
    }),
  }));
}

export const TransactionsTable: React.FC<TransactionsTableProps> = (props) => {
  const { loading, error } = props;

  const isPaginated = !!props.pages;

  if (!isPaginated) {
    return (
      <SimpleTransactionsTable
        transactions={props.transactions}
        loading={loading}
        error={error}
      />
    );
  }

  return (
    <PaginatedTransactionsTable
      siteId={props.siteId}
      pages={props.pages}
      loading={loading}
      error={error}
      fetchNextPage={props.fetchNextPage}
      hasNextPage={props.hasNextPage}
      isFetchingNextPage={props.isFetchingNextPage}
      onFilterChange={props.onFilterChange}
    />
  );
};

// Shared render body: takes a page of already-grouped transactions plus the
// full loaded set (for building filter option lists) and renders the
// expandable table + pagination control. Both the paginated (server-fetched)
// and simple (fully client-side) variants funnel through this so the two
// views stay visually identical.
const GroupedTransactionsView: React.FC<{
  pageTransactions: TransactionViewDto[];
  allTransactionsForFilters: TransactionViewDto[];
  loading: boolean;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number, pageSize: number) => void;
  onTableChange?: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
  ) => void;
  showCalculatedAmount?: boolean;
  endAsGhostSiteId?: string;
}> = ({
  pageTransactions,
  allTransactionsForFilters,
  loading,
  currentPage,
  pageSize,
  total,
  onPageChange,
  onTableChange,
  showCalculatedAmount = false,
  endAsGhostSiteId,
}) => {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggleExpand = useCallback((txId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(txId)) {
        next.delete(txId);
      } else {
        next.add(txId);
      }
      return next;
    });
  }, []);

  const flatRows = useMemo(
    () => flattenWithEvents(pageTransactions, expandedIds),
    [pageTransactions, expandedIds],
  );

  const columns = useMemo(
    () =>
      groupedColumns(
        allTransactionsForFilters,
        expandedIds,
        showCalculatedAmount,
        endAsGhostSiteId,
      ),
    [
      allTransactionsForFilters,
      expandedIds,
      showCalculatedAmount,
      endAsGhostSiteId,
    ],
  );

  return (
    <>
      <Table<FlatRow>
        columns={columns}
        className="w-full"
        dataSource={flatRows}
        loading={loading}
        pagination={false}
        rowKey="_key"
        onChange={onTableChange}
        onRow={(record) => ({
          onClick:
            record._kind === "tx"
              ? () => toggleExpand(record.transaction_id)
              : undefined,
          style:
            record._kind === "tx"
              ? { cursor: "pointer" }
              : { backgroundColor: "#fafafa", cursor: "default" },
        })}
      />
      <div className="flex justify-end mt-4">
        <Pagination
          current={currentPage}
          total={total}
          pageSize={pageSize}
          onChange={onPageChange}
          showSizeChanger={false}
        />
      </div>
    </>
  );
};

const SimpleTransactionsTable: React.FC<{
  transactions: TransactionReadable[];
  loading: boolean;
  error: boolean;
}> = ({ transactions, loading, error }) => {
  const {
    pageTransactions,
    groupedTransactions,
    currentPage,
    pageSize,
    total,
    onPageChange,
  } = useGroupedTransactions(transactions);

  if (error) {
    return <ErrorMessage />;
  }

  return (
    <GroupedTransactionsView
      pageTransactions={pageTransactions}
      allTransactionsForFilters={groupedTransactions}
      loading={loading}
      currentPage={currentPage}
      pageSize={pageSize}
      total={total}
      onPageChange={onPageChange}
    />
  );
};

const PaginatedTransactionsTable: React.FC<PaginatedTransactionsTableProps> = (
  props,
) => {
  const { loading, error } = props;

  const cursorPagination = useCursorPagination<TransactionViewDto>({
    pages: props.pages,
    hasNextPage: props.hasNextPage,
    fetchNextPage: props.fetchNextPage,
    isFetchingNextPage: props.isFetchingNextPage,
  });

  const transactions = cursorPagination.dataSource;

  const pageSize = cursorPagination.pagination.pageSize ?? 20;
  const currentPage = cursorPagination.pagination.current ?? 1;

  const pageTransactions = useMemo(
    () =>
      transactions.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [transactions, currentPage, pageSize],
  );

  const handleTableChange = useCallback(
    (
      pagination: TablePaginationConfig,
      filters: Record<string, FilterValue | null>,
    ) => {
      cursorPagination.onTableChange(pagination, filters);
      cursorPagination.pagination.onChange?.(1, pageSize);

      if (props.onFilterChange) {
        props.onFilterChange({
          asset_id: filters.asset_id as string[] | undefined,
          profile_id: filters.profile_id as string[] | undefined,
        });
      }
    },
    [props, cursorPagination, pageSize],
  );

  if (error) {
    return <ErrorMessage />;
  }

  return (
    <GroupedTransactionsView
      pageTransactions={pageTransactions}
      allTransactionsForFilters={transactions}
      loading={loading || cursorPagination.isPageLoading}
      currentPage={currentPage}
      pageSize={pageSize}
      total={transactions.length + (props.hasNextPage ? 1 : 0)}
      onPageChange={cursorPagination.pagination.onChange!}
      onTableChange={handleTableChange}
      showCalculatedAmount
      endAsGhostSiteId={isViewAsAdmin() ? props.siteId : undefined}
    />
  );
};
